import { Download, Smartphone, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { isMainAppPath } from '../lib/backNavigation';
import { useSessionStore } from '../stores/session-store';
import styles from './PwaInstallPrompt.module.css';

type BeforeInstallPromptOutcome = {
  outcome: 'accepted' | 'dismissed';
  platform: string;
};

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<BeforeInstallPromptOutcome>;
}

const DISMISSED_UNTIL_KEY = 'aeterna_pwa_install_dismissed_until';
const COMPLETED_KEY = 'aeterna_pwa_install_completed';
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

function getPlatform() {
  const ua = window.navigator.userAgent;
  const isiPadOs = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
  const isIos = /iPad|iPhone|iPod/.test(ua) || isiPadOs;
  const isIosChrome = isIos && /CriOS/.test(ua);
  const isIosSafari = isIos && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  const isAndroid = /Android/.test(ua);
  return { isAndroid, isIos, isIosChrome, isIosSafari };
}

function isStandaloneDisplay() {
  const navigatorWithStandalone = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || navigatorWithStandalone.standalone === true;
}

function readDismissed() {
  try {
    if (window.localStorage.getItem(COMPLETED_KEY) === '1') return true;
    const dismissedUntil = Number(window.localStorage.getItem(DISMISSED_UNTIL_KEY) ?? '0');
    return Number.isFinite(dismissedUntil) && dismissedUntil > Date.now();
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    window.localStorage.setItem(DISMISSED_UNTIL_KEY, String(Date.now() + DISMISS_MS));
  } catch {
    /* storage is best-effort */
  }
}

function writeCompleted() {
  try {
    window.localStorage.setItem(COMPLETED_KEY, '1');
  } catch {
    /* storage is best-effort */
  }
}

export function PwaInstallPrompt() {
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isAuthenticated = useSessionStore((state) => state.isAuthenticated);
  const isLoading = useSessionStore((state) => state.isLoading);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [ready, setReady] = useState(false);

  const platform = useMemo(() => {
    if (typeof window === 'undefined') {
      return { isAndroid: false, isIos: false, isIosChrome: false, isIosSafari: false };
    }
    return getPlatform();
  }, []);

  useEffect(() => {
    setDismissed(readDismissed());
    const timer = window.setTimeout(() => setReady(true), 1400);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };
    const handleInstalled = () => {
      writeCompleted();
      setDismissed(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  if (
    !ready ||
    !isMobile ||
    isLoading ||
    !isAuthenticated ||
    dismissed ||
    isStandaloneDisplay()
  ) {
    return null;
  }

  const shouldShow = deferredPrompt
    || platform.isIos
    || platform.isAndroid
    || location.pathname.startsWith('/execute/longevity/day')
    || location.pathname.startsWith('/profile');

  if (!shouldShow) return null;

  const guideText = platform.isIosChrome
    ? 'iPhone 上 Chrome 不能直接添加。用 Safari 打开后，点分享，再选添加到主屏幕。'
    : platform.isIosSafari
      ? '点分享按钮，再选添加到主屏幕。以后可从桌面直接进入今天的实践。'
      : deferredPrompt
        ? '添加后可从桌面直接打开，少一层浏览器栏。'
        : '从浏览器菜单选择添加到主屏幕，之后可像 App 一样打开。';

  const close = () => {
    writeDismissed();
    setDismissed(true);
  };

  const install = async () => {
    if (!deferredPrompt) {
      close();
      return;
    }
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      writeCompleted();
      setDismissed(true);
    } else {
      close();
    }
    setDeferredPrompt(null);
  };

  return (
    <aside className={`${styles.prompt} ${isMainAppPath(location.pathname) ? styles.aboveTabs : ''}`} aria-label="添加到主屏幕">
      <div className={styles.icon} aria-hidden>
        <Smartphone size={18} strokeWidth={1.5} />
      </div>
      <div className={styles.copy}>
        <p className={styles.title}>添加到主屏幕</p>
        <p className={styles.text}>{guideText}</p>
      </div>
      <div className={styles.actions}>
        {deferredPrompt && (
          <button className={styles.primaryButton} onClick={install}>
            <Download size={15} strokeWidth={1.6} aria-hidden />
            添加
          </button>
        )}
        <button className={styles.closeButton} onClick={close} aria-label="稍后再说">
          <X size={16} strokeWidth={1.6} aria-hidden />
        </button>
      </div>
    </aside>
  );
}
